<template>
  <div class="questionnaire-preview" :class="{ 'selector-preview': showQuestionLabel, 'invitation-preview': !showQuestionLabel }">
    <div
      v-for="(item, index) in items"
      :key="item.id || item.questionId || index"
      class="preview-question"
    >
      <div class="preview-order">
        <span class="order-number" :class="{ 'is-double-digit': index + 1 >= 10 }">{{ index + 1 }}</span>
        <div class="sloping-side"></div>
      </div>
      <div class="preview-card">
        <div class="preview-main">
          <div class="preview-title">
            <span v-if="showQuestionLabel" class="question-label">{{ $t(`${i18nPrefix}.questionLabel`, { index: index + 1 }) }}</span>
            <span class="question-text">{{ getQuestionTitle(item) }}</span>
          </div>
          <div class="question-type-pill">
            <i :class="getTypeIcon(getQuestionType(item))"></i>
            {{ getTypeLabel(getQuestionType(item)) }}
          </div>
        </div>
        <div v-if="showWeight(item)" class="question-weighting">{{ $t(`${i18nPrefix}.weighting`, { weight: item.weight }) }}</div>
        <div v-if="getQuestionOptions(item).length > 0" class="question-options">
          <div v-if="showQuestionLabel" class="answer-label">{{ $t(`${i18nPrefix}.answerLabel`) }}</div>
          <div class="option-list">
            <div
              v-for="(option, optionIndex) in getQuestionOptions(item)"
              :key="option.id || optionIndex"
              class="preview-option"
            >
              <span class="option-control" :class="{ 'is-radio': isSingleSelect(item) }"></span>
              <span class="option-text">{{ getOptionLabel(option) }}</span>
            </div>
          </div>
        </div>
        <div v-if="showInterviewerAssignment" class="question-interviewer-assignment">
          <div class="interviewer-label">{{ $t(`${i18nPrefix}.interviewer`) }}</div>
          <div
            class="interviewer-select-container"
            :class="{ 'is-error': isInterviewerAssignmentInvalid(index) }"
          >
            <CustomSelect
              class="interviewer-select"
              size="small"
              filterable
              clearable
              :value="getQuestionInterviewerKeys(index)"
              :options="hmOptions"
              label-key="label"
              value-key="key"
              :select-all-label="$t(`${i18nPrefix}.selectAll`)"
              @change="handleInterviewerAssignmentChange(index, $event)"
            />
            <div v-if="isInterviewerAssignmentInvalid(index)" class="assignment-error">
              {{ $t('common.message.requiredFormField') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { getI18nPropertyValue } from '@/i18n'
import { HM_QUESTION_TYPE } from '@/constant/questionnaire'
import CustomSelect from '@/components/formEditor/customSelect.vue'

export default {
  components: {
    CustomSelect
  },
  props: {
    questionnaire: {
      type: Object,
      default: null
    },
    items: {
      type: Array,
      default: () => []
    },
    showQuestionLabel: {
      type: Boolean,
      default: false
    },
    showInterviewerAssignment: {
      type: Boolean,
      default: false
    },
    hmOptions: {
      type: Array,
      default: () => []
    },
    questionKeys: {
      type: Array,
      default: () => []
    },
    questionInterviewerMap: {
      type: Object,
      default: () => ({})
    },
    showInterviewerAssignmentErrors: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList.questionnairePreview'
    }
  },
  methods: {
    getQuestionInterviewerKeys (index) {
      return this.questionInterviewerMap[this.questionKeys[index]] || []
    },
    isInterviewerAssignmentInvalid (index) {
      return this.showInterviewerAssignmentErrors && _.isEmpty(this.getQuestionInterviewerKeys(index))
    },
    handleInterviewerAssignmentChange (index, hmKeys) {
      this.$emit('interviewer-assignment-change', {
        questionKey: this.questionKeys[index],
        hmKeys
      })
    },
    getQuestionTitle (item) {
      const question = item.question || item
      return getI18nPropertyValue(question, 'title_zh', 'title_en') || question.title || question.name
    },
    getQuestionType (item) {
      return _.get(item, 'question.type') || item.type
    },
    getTypeLabel (type) {
      const typeKey = _.toLower(_.findKey(HM_QUESTION_TYPE, value => value === type))
      return typeKey ? this.$t(`${this.i18nPrefix}.questionType.${typeKey}`) : type
    },
    getTypeIcon (type) {
      const iconMap = {
        [HM_QUESTION_TYPE.RATING]: 'icona-Star2Stroke',
        [HM_QUESTION_TYPE.TEXT]: 'iconText',
        [HM_QUESTION_TYPE.SINGLE]: 'icona-SingleSelect',
        [HM_QUESTION_TYPE.MULTIPLE]: 'icona-multipleanswer1',
        [HM_QUESTION_TYPE.DATE]: 'iconCalendar1'
      }
      return ['iconfont', iconMap[type]]
    },
    getQuestionOptions (item) {
      return _.get(item, 'question.options') || item.options || []
    },
    getOptionLabel (option) {
      return getI18nPropertyValue(option, 'label_zh', 'label_en')
    },
    showWeight (item) {
      return this.getQuestionType(item) === HM_QUESTION_TYPE.RATING && item.weight
    },
    isSingleSelect (item) {
      return this.getQuestionType(item) === HM_QUESTION_TYPE.SINGLE
    }
  }
}
</script>

<style lang="scss" scoped>
.questionnaire-preview {
  .preview-question {
    display: flex;
    align-items: flex-start;
    margin-bottom: 0;

    .preview-order {
      width: 32px;
      flex: 0 0 32px;
      color: #0096cc;
      font-size: 20px;
      line-height: 24px;

      .order-number {
        display: inline-block;

        &.is-double-digit {
          transform: translateX(-10px);
        }
      }

      .sloping-side {
        width: 24px;
        border-bottom: 1px solid #0096cc;
        transform: rotate(121deg);
        margin: -2px 0 0 1px;
      }
    }

    .preview-card {
      flex: 1;
      min-height: 44px;
      padding: 12px 20px 0;
      background: #f7f7f7;

      .preview-main {
        display: flex;
        align-items: flex-start;
        gap: 50px;
        padding-bottom: 13px;
        border-bottom: 1px solid #dfdfdf;

        .preview-title {
          flex: 1;
          color: #606266;
          font-size: 14px;
          line-height: 19px;

          .question-label {
            margin-right: 4px;
          }

          .question-text {
            word-break: break-word;
            white-space: pre-wrap;
          }
        }

        .question-type-pill {
          flex: 0 0 auto;
          color: #0096cc;
          font-size: 14px;
          line-height: 19px;

          i {
            margin-right: 4px;
          }
        }
      }

      .question-weighting {
        padding: 12px 0 0;
        color: #606266;
        font-size: 14px;
        line-height: 19px;
      }

      .question-options {
        padding: 12px 0;
        color: #606266;
        font-size: 14px;
        line-height: 19px;

        .answer-label {
          margin-bottom: 8px;
        }

        .option-list {
          display: flex;
          flex-direction: column;
          gap: 8px;

          .preview-option {
            display: flex;
            align-items: center;
            gap: 8px;

            .option-control {
              width: 14px;
              height: 14px;
              flex: 0 0 14px;
              border: 1px solid #c0c4cc;
              border-radius: 2px;
              background: #f1f5f9;

              &.is-radio {
                border-radius: 50%;
              }
            }

            .option-text {
              color: #606266;
              font-size: 14px;
              line-height: 14px;
            }
          }
        }
      }
    }
  }

  &.selector-preview {
    .preview-question {
      padding-top: 16px;

      .preview-order {
        display: none;
      }

      .preview-card {
        min-height: auto;
        padding: 0;
        background: transparent;

        .preview-main {
          align-items: center;
          gap: 0;
          padding-bottom: 0;
          border-bottom: 0;

          .preview-title {
            display: flex;
            flex: 1;
            align-items: flex-start;
            gap: 9px;
            color: #606266;
            font-size: 14px;
            line-height: 14px;

            .question-label {
              flex: 0 0 90px;
              margin-right: 0;
              line-height: 14px;
            }

            .question-text {
              flex: 1;
              line-height: 16px;
              word-break: break-word;
              white-space: pre-wrap;
            }
          }

          .question-type-pill {
            padding: 4px 8px;
            border: 1px solid #cdecf7;
            border-radius: 21px;
            background: #f1fbff;
            font-size: 10px;
            line-height: 10px;
            font-weight: 500;

            i {
              margin-right: 0;
              font-size: 12px;
              line-height: 12px;
            }
          }
        }

        .question-weighting {
          margin: 8px 0 0 99px;
          padding: 0;
          color: #0096cc;
          font-size: 10px;
          line-height: 12px;
        }

        .question-options {
          display: flex;
          align-items: flex-start;
          gap: 37px;
          margin-top: 8px;
          padding: 0;
          color: #606266;
          font-size: 14px;
          line-height: 14px;

          .answer-label {
            flex: 0 0 62px;
            margin-bottom: 0;
            line-height: 14px;
            white-space: nowrap;
          }

          .option-list {
            padding-left: 20px;
            gap: 16px;

            .preview-option {
              gap: 8px;
            }
          }
        }
      }
    }
  }

  &.invitation-preview {
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr);
    align-items: stretch;

    .preview-question {
      display: contents;

      .preview-order {
        grid-column: 1;
        padding-top: 0;
      }

      .preview-card {
        position: relative;
        grid-column: 2;
        min-height: 47px;
        padding: 12px 20px 13px;

        .preview-main {
          align-items: flex-start;
          gap: 50px;
          padding-bottom: 0;
          border-bottom: 0;

          .preview-title {
            line-height: 16px;

            .question-text {
              word-break: break-word;
              white-space: pre-wrap;
            }
          }

          .question-type-pill {
            padding: 4px 8px;
            border: 1px solid #cdecf7;
            border-radius: 21px;
            background: #f1fbff;
            font-size: 10px;
            line-height: 10px;
            font-weight: 500;

            i {
              font-size: 12px;
              line-height: 12px;
            }
          }
        }

        .question-weighting {
          margin-top: 4px;
          padding: 0;
          color: #0096cc;
          font-size: 10px;
          line-height: 12px;
        }

        .question-options {
          padding: 8px 0 0;

          .option-list {
            gap: 16px;
          }
        }

        .question-interviewer-assignment {
          display: flex;
          align-items: flex-start;
          gap: 9px;
          padding-top: 8px;

          .interviewer-label {
            flex: 0 0 73px;
            color: #303133;
            font-size: 14px;
            font-weight: 500;
            line-height: 32px;
          }

          .interviewer-select-container {
            flex: 1;
            min-width: 0;

            .interviewer-select {
              width: 100%;
            }

            &.is-error {
              ::v-deep {
                .el-input__inner {
                  border-color: #f56c6c;
                }
              }
            }

            .assignment-error {
              margin-top: 4px;
              color: #f56c6c;
              font-size: 12px;
              line-height: 16px;
            }
          }
        }
      }

      &:not(:last-child) {
        .preview-card::after {
          content: '';
          position: absolute;
          right: 20px;
          bottom: 0;
          left: 20px;
          border-bottom: 1px solid #dfdfdf;
        }
      }
    }
  }
}
</style>
